/**
 * API-сервис обмена данными: импорт/экспорт/синхронизация.
 *
 * Обёртка над `api-client-v2` для контрактов `/api/v1/import|export|sync`.
 * Код справочников xlsx/zip распространяется через backend-сервисы (единый источник KLS),
 * здесь — только работа с эндпоинтами.
 */

import {
  apiRequest,
  get,
  post,
  postFormData,
  downloadFile,
  API_BASE_URL,
} from "@/lib/api-client-v2";
import type {
  ImportBatchBrief,
  ImportBatchStatusOut,
  ImportBatchReportOut,
  ImportRowErrorOut,
  DataExchangeReport,
  ExportRequest,
  ExportJobResume,
  SyncPartnerCreate,
  SyncPartnerUpdate,
  SyncPartnerResume,
  SyncHistoryOut,
} from "@/types/api-v2";

export interface ImportUploadOptions {
  mode: "objects" | "template" | "sync";
  strategy: "strict" | "auto";
  dryRun: boolean;
  password?: string;
  groupId?: string;
  appendGeometries?: boolean;
  onProgress?: (percent: number) => void;
}

class DataExchangeApiService {
  // ---------- Импорт ----------

  async uploadImport(
    files: File[],
    options: ImportUploadOptions,
  ): Promise<ImportBatchStatusOut | ImportBatchStatusOut[]> {
    const form = new FormData();
    for (const file of files) {
      form.append("files", file);
    }
    form.append("mode", options.mode);
    form.append("strategy", options.strategy);
    form.append("dry_run", String(options.dryRun));
    if (options.password) form.append("password", options.password);
    if (options.groupId) form.append("group_id", options.groupId);
    form.append("append_geometries", String(options.appendGeometries ?? false));

    if (options.onProgress) {
      return new Promise<ImportBatchStatusOut | ImportBatchStatusOut[]>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_BASE_URL}/import/batches`);
        const token = localStorage.getItem("access_token");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) options.onProgress?.(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error("Некорректный ответ сервера"));
            }
          } else {
            try {
              const body = JSON.parse(xhr.responseText);
              reject(new Error(body?.detail ?? `HTTP ${xhr.status}`));
            } catch {
              reject(new Error(`HTTP ${xhr.status}`));
            }
          }
        };
        xhr.onerror = () => reject(new Error("Сетевая ошибка при загрузке"));
        xhr.send(form);
      });
    }
    return postFormData<ImportBatchStatusOut | ImportBatchStatusOut[]>("/import/batches", form);
  }

  async listBatches(params?: {
    status?: string;
    mode?: string;
    limit?: number;
    skip?: number;
  }): Promise<ImportBatchBrief[]> {
    return get<ImportBatchBrief[]>("/import/batches", {
      status: params?.status ?? "",
      mode: params?.mode ?? "",
      limit: params?.limit ?? 100,
      skip: params?.skip ?? 0,
    });
  }

  async getBatch(batchId: number): Promise<ImportBatchStatusOut> {
    return get<ImportBatchStatusOut>(`/import/batches/${batchId}`);
  }

  async getBatchReport(batchId: number): Promise<ImportBatchReportOut> {
    return get<ImportBatchReportOut>(`/import/batches/${batchId}/report`);
  }

  async getBatchErrors(
    batchId: number,
    skip = 0,
    limit = 200,
  ): Promise<ImportRowErrorOut[]> {
    return get<ImportRowErrorOut[]>(`/import/batches/${batchId}/errors`, { skip, limit });
  }

  async commitBatch(batchId: number): Promise<ImportBatchReportOut> {
    return post<ImportBatchReportOut>(`/import/batches/${batchId}/commit`, {});
  }

  async commitGroup(groupId: string): Promise<ImportBatchReportOut[]> {
    return post<ImportBatchReportOut[]>(`/import/batches/group/${groupId}/commit`, {});
  }

  // ---------- Экспорт ----------

  async createExportJob(body: ExportRequest): Promise<ExportJobResume> {
    return post<ExportJobResume>("/export/jobs", body);
  }

  async getExportJob(jobId: number): Promise<ExportJobResume> {
    return get<ExportJobResume>(`/export/jobs/${jobId}`);
  }

  async downloadTemplate(): Promise<Blob> {
    return downloadFile("/export/template/download");
  }

  async downloadExportPackage(jobId: number): Promise<Blob> {
    return downloadFile(`/export/jobs/${jobId}/download`);
  }

  // ---------- Синхронизация ----------

  async listSyncPartners(): Promise<SyncPartnerResume[]> {
    return get<SyncPartnerResume[]>("/sync/partners");
  }

  async createSyncPartner(body: SyncPartnerCreate): Promise<SyncPartnerResume> {
    return apiRequest<SyncPartnerResume>("/sync/partners", {
      method: "POST",
      data: body,
    });
  }

  async updateSyncPartner(
    partnerId: number,
    body: SyncPartnerUpdate,
  ): Promise<SyncPartnerResume> {
    return apiRequest<SyncPartnerResume>(`/sync/partners/${partnerId}`, {
      method: "PATCH",
      data: body,
    });
  }

  async uploadSyncPackage(
    partnerId: number,
    file: File,
    dryRun: boolean,
  ): Promise<DataExchangeReport> {
    const form = new FormData();
    form.append("file", file);
    form.append("dry_run", String(dryRun));
    return postFormData<DataExchangeReport>(`/sync/partners/${partnerId}/upload`, form);
  }

  async listSyncHistory(partnerId?: number, limit = 100): Promise<SyncHistoryOut[]> {
    return get<SyncHistoryOut[]>("/sync/history", {
      partner_id: partnerId ?? "",
      limit,
    });
  }

  async downloadSyncDelta(partnerId: number): Promise<Blob> {
    return downloadFile(`/sync/partners/${partnerId}/delta-export`);
  }
}

export const dataExchangeApi = new DataExchangeApiService();